import type { LeadSource } from '@nexofacil/schemas';
import { captureSource } from './url-source.js';

// =============================================================================
// Persistência da origem do tráfego durante a sessão.
//
// Por que existe: o anúncio manda o visitante pra landing do corretor (`/`) e
// só depois ele chega no formulário, via navegação interna do React Router.
// Essa navegação não carrega a query string, então ler os UTMs no momento em
// que o form monta captura uma URL já limpa · era por isso que 172 leads em
// 90 dias tinham `source.utmCampaign` vazio.
//
// A solução: gravar a origem no PRIMEIRO pageview (onde os parâmetros ainda
// existem) e o form ler daqui. Cobre os três caminhos de entrada:
//   anúncio → landing → form
//   anúncio → landing → empreendimento → form
//   anúncio → empreendimento → form
// =============================================================================

const STORAGE_KEY = 'nexofacil:source';

/** Campos que caracterizam uma chegada de campanha (vs. navegação interna). */
function hasCampaignSignal(s: LeadSource): boolean {
  return Boolean(s.utmSource || s.utmCampaign || s.utmMedium || s.utmTerm || s.utmContent || s.fbclid);
}

function read(): LeadSource | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as LeadSource) : null;
  } catch {
    // Safari em modo privado e navegadores com storage bloqueado caem aqui.
    // Perder atribuição é aceitável; quebrar o formulário não é.
    return null;
  }
}

function write(source: LeadSource): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(source));
  } catch {
    // idem
  }
}

/**
 * Chamado uma vez no boot do app (App.tsx), antes de qualquer navegação.
 *
 * Só sobrescreve o que já estava guardado quando a URL atual traz sinal de
 * campanha. Assim um F5 no meio do funil não apaga a origem, mas um clique
 * novo num outro anúncio (que recarrega a página com novos parâmetros)
 * reatribui o visitante corretamente.
 */
export function captureSourceOnce(): void {
  const fromUrl = captureSource();
  const stored = read();

  if (stored && !hasCampaignSignal(fromUrl)) return;

  write({
    ...fromUrl,
    // Referrer e URL de entrada valem do primeiro pageview: depois da
    // navegação interna o referrer vira a própria landing, o que não diz nada.
    referrer: fromUrl.referrer,
    entryUrl: window.location.href,
  });
}

/**
 * Origem a enviar no submit. Combina o que foi guardado na entrada com a URL
 * atual, que sempre vence quando também traz parâmetros (caso do visitante
 * que cai direto em `/qualificar?utm_...`).
 *
 * `landingUrl` continua sendo a URL do formulário, e não a de entrada · o
 * Meta CAPI usa esse campo como `eventSourceUrl` e ele precisa apontar pra
 * página onde o evento de fato aconteceu.
 */
export function getLeadSource(): LeadSource {
  const stored = read() ?? {};
  const fromUrl = captureSource();
  const merged: LeadSource = hasCampaignSignal(fromUrl) ? { ...stored, ...fromUrl } : { ...stored };

  return {
    ...merged,
    referrer: stored.referrer ?? fromUrl.referrer,
    entryUrl: stored.entryUrl ?? window.location.href,
    landingUrl: window.location.href,
  };
}
